/**
 * Case Subtype Taxonomy — admin API client.
 *
 * Wraps the backend routes in `/api/v1/case-subtype-taxonomy/*`:
 *   • GET  /options                           — resolver-backed dropdown feed
 *   • POST /resolve                           — single-subtype resolution
 *   • GET  /mappings                          — admin review queue (status filter)
 *   • POST /mappings/:id/approve              — admin approve mapping
 *   • POST /mappings/:id/reject               — admin reject mapping
 *   • GET  /form-bindings                     — admin review queue for form bindings
 *   • POST /form-bindings/:id/approve         — admin approve binding
 *   • POST /form-bindings/:id/reject          — admin reject binding
 *   • GET  /coverage                          — coverage + blocker summary
 *   • POST /discover-from-url                 — semi-manual URL discovery
 */

const TAXONOMY_API_BASE = '/api/v1/case-subtype-taxonomy';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('webelieve_access_token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';
  let payload: any = null;
  if (text) {
    if (contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('[')) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }
  }
  if (!response.ok) {
    const message =
      (payload && typeof payload === 'object' && (payload.description || payload.message)) ||
      `Request failed (${response.status}).`;
    throw new Error(message);
  }
  if (payload == null) return {} as T;
  return payload as T;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v).length > 0) usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── Types ───

export type CaseSubtypeLifecycle = 'pending' | 'validated' | 'stale' | 'rejected';
export type CaseSubtypeScope = 'national' | 'state' | 'court';

export interface CaseSubtypeMapping {
  id: string;
  practice_area: string;
  canonical_subtype_id?: string | null;
  canonical_key: string;
  scope_type: CaseSubtypeScope;
  state_code?: string | null;
  jurisdiction_code?: string | null;
  local_key: string;
  local_label: string;
  local_description?: string | null;
  source_authority?: string | null;
  source_url?: string | null;
  source_citation?: string | null;
  source_effective_date?: string | null;
  confidence_score?: string | null;
  lifecycle_status: CaseSubtypeLifecycle;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_notes?: string | null;
  is_active: boolean;
  /** Letter-for-letter default display term for this canonical in this scope. */
  is_default_for_scope?: boolean;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export type CaseSubtypeFormRelationType = 'mandatory' | 'optional' | 'conditional' | 'packet';
export type CaseSubtypeFormResolutionStatus = 'unresolved_candidate' | 'resolved' | 'court_form_deleted';

export interface CaseSubtypeFormBinding {
  id: string;
  case_subtype_mapping_id?: string | null;
  canonical_key: string;
  jurisdiction_code?: string | null;
  state_code?: string | null;
  court_form_id?: string | null;
  form_resolution_status: CaseSubtypeFormResolutionStatus;
  form_number?: string | null;
  form_name?: string | null;
  relation_type: CaseSubtypeFormRelationType;
  condition_text?: string | null;
  source_url?: string | null;
  confidence_score?: string | null;
  lifecycle_status: CaseSubtypeLifecycle;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CoverageGapRow {
  id: string;
  practice_area: string;
  state_code: string | null;
  jurisdiction_code: string | null;
  canonical_key: string;
  local_label: string;
  local_key: string;
}

export interface TaxonomyCoverageReport {
  filters: { state_code?: string; jurisdiction_code?: string; practice_area?: string; include_gap_rows?: boolean };
  case_subtype_pending_mapping_count: number;
  case_subtype_validated_mapping_count: number;
  case_subtype_rejected_mapping_count: number;
  form_binding_pending_count: number;
  form_binding_validated_count: number;
  form_binding_with_drift_count: number;
  validated_mappings_without_form_bindings: number;
  validated_mappings_without_workflow: number;
  validated_mappings_without_template: number;
  validated_mappings_without_playbook_scheme: number;
  taxonomy_blockers: Array<{ code: string; count: number; detail: string }>;
  gap_rows?: {
    validated_mappings_without_form_bindings: CoverageGapRow[];
    validated_mappings_without_workflow: CoverageGapRow[];
    validated_mappings_without_template: CoverageGapRow[];
    validated_mappings_without_playbook_scheme: CoverageGapRow[];
  };
}

export interface SubtypeOption {
  key: string;
  label: string;
  description?: string;
  form_count?: number;
  template_id?: string | null;
  template_status?: string | null;
  canonical_key: string;
  mapping_id?: string | null;
  local_key?: string | null;
  local_label?: string | null;
  source_scope?: 'court' | 'state' | 'national' | 'legacy' | 'json';
  resolution_status?: 'validated' | 'legacy_fallback' | 'json_fallback';
  warnings?: string[];
}

export interface UrlDiscoveryResult {
  url: string;
  dry_run: boolean;
  raw_label_count: number;
  filtered_label_count: number;
  harvested_labels: string[];
  discarded: Array<{ reason: string; evidence: Record<string, unknown> }>;
  producer_result: {
    dry_run: boolean;
    projected_mappings: Array<{
      action: 'insert' | 'update' | 'skip_existing_covered';
      practice_area: string;
      scope_type: 'court' | 'state';
      state_code?: string | null;
      jurisdiction_code?: string | null;
      local_key: string;
      local_label: string;
      canonical_key: string;
      canonical_subtype_id?: string | null;
      reason: string;
      existing_mapping_id?: string | null;
    }>;
    projected_bindings: Array<{
      action: 'insert' | 'update' | 'skip_existing_covered';
      local_key: string;
      form_number: string | null;
      court_form_id: string | null;
      existing_binding_id?: string | null;
      reason: string;
    }>;
    applied: {
      mapping_inserts: number;
      mapping_updates: number;
      binding_inserts: number;
      binding_updates: number;
    };
  };
}

// ─── API functions ───

export async function listOptions(filters: {
  practice_area: string;
  jurisdiction_code?: string;
  state_code?: string;
}): Promise<{ options: SubtypeOption[] }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/options${buildQuery(filters)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<{ options: SubtypeOption[] }>(response);
}

export async function listMappings(filters: {
  status?: CaseSubtypeLifecycle | 'all';
  jurisdiction_code?: string;
  state_code?: string;
  practice_area?: string;
  limit?: number;
}): Promise<{ mappings: CaseSubtypeMapping[] }> {
  const q = { ...filters, status: filters.status === 'all' ? undefined : filters.status };
  const response = await fetch(`${TAXONOMY_API_BASE}/mappings${buildQuery(q)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<{ mappings: CaseSubtypeMapping[] }>(response);
}

export async function approveMapping(
  id: string,
  body: { review_notes?: string; evidence_exception?: string },
): Promise<{ mapping: CaseSubtypeMapping }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/mappings/${id}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseResponse<{ mapping: CaseSubtypeMapping }>(response);
}

export async function rejectMapping(
  id: string,
  body: { review_notes?: string },
): Promise<{ mapping: CaseSubtypeMapping }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/mappings/${id}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseResponse<{ mapping: CaseSubtypeMapping }>(response);
}

/**
 * Mark a validated mapping as the scope's default display term. Unsets any
 * other sibling mapping in the same (practice_area, scope, state/jurisdiction,
 * canonical_key) group.
 */
export async function setMappingDefault(
  id: string,
): Promise<{ mapping: CaseSubtypeMapping }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/mappings/${id}/set-default`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  return parseResponse<{ mapping: CaseSubtypeMapping }>(response);
}

export async function listFormBindings(filters: {
  status?: CaseSubtypeLifecycle | 'all';
  jurisdiction_code?: string;
  state_code?: string;
  canonical_key?: string;
  /** When true, only validated bindings with form_number drift are returned. */
  has_drift?: boolean;
  limit?: number;
}): Promise<{ form_bindings: CaseSubtypeFormBinding[] }> {
  const q: Record<string, string | number | undefined> = {
    ...filters,
    status: filters.status === 'all' ? undefined : filters.status,
    has_drift: filters.has_drift ? 'true' : undefined,
  };
  const response = await fetch(`${TAXONOMY_API_BASE}/form-bindings${buildQuery(q)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<{ form_bindings: CaseSubtypeFormBinding[] }>(response);
}

export async function approveFormBinding(
  id: string,
  body: { review_notes?: string; evidence_exception?: string },
): Promise<{ form_binding: CaseSubtypeFormBinding }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/form-bindings/${id}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseResponse<{ form_binding: CaseSubtypeFormBinding }>(response);
}

export async function rejectFormBinding(
  id: string,
  body: { review_notes?: string },
): Promise<{ form_binding: CaseSubtypeFormBinding }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/form-bindings/${id}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseResponse<{ form_binding: CaseSubtypeFormBinding }>(response);
}

export async function getCoverage(filters: {
  state_code?: string;
  jurisdiction_code?: string;
  practice_area?: string;
  /** When true, the response includes gap_rows drilldown lists for each downstream gap. */
  include_gap_rows?: boolean;
}): Promise<{ coverage: TaxonomyCoverageReport }> {
  const q: Record<string, string | number | undefined> = {
    state_code: filters.state_code,
    jurisdiction_code: filters.jurisdiction_code,
    practice_area: filters.practice_area,
    include: filters.include_gap_rows ? 'gap_rows' : undefined,
  };
  const response = await fetch(`${TAXONOMY_API_BASE}/coverage${buildQuery(q)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<{ coverage: TaxonomyCoverageReport }>(response);
}

export async function discoverFromUrl(body: {
  url: string;
  practice_area?: string | null;
  jurisdiction_code?: string | null;
  state_code?: string | null;
  dry_run?: boolean;
  /**
   * When true, the extractor runs harvested labels through the cross-state
   * alias heuristic (e.g. "divorce" → "dissolution"). Default false so the
   * proposed canonical matches the jurisdiction letter-for-letter.
   */
  allow_cross_state_alias?: boolean;
}): Promise<{ discovery: UrlDiscoveryResult }> {
  const response = await fetch(`${TAXONOMY_API_BASE}/discover-from-url`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return parseResponse<{ discovery: UrlDiscoveryResult }>(response);
}

// ─── Helpers ───

export const LIFECYCLE_COLORS: Record<CaseSubtypeLifecycle, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  validated: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  stale: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  rejected: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
};

export const LIFECYCLE_LABELS: Record<CaseSubtypeLifecycle, string> = {
  pending: 'Pending Review',
  validated: 'Validated',
  stale: 'Stale',
  rejected: 'Rejected',
};
